/**
 * LanguageSection Component
 * Select preferred language
 */

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useUpdateUserSettings } from "../hooks/useSettings"
import { getLanguages } from "../utils/language.utils"

export function LanguageSection() {
  const { t, i18n } = useTranslation(["settings", "common"])
  const user = useAuthStore(state => state.user)
  const { mutate: updateSettings, isPending } = useUpdateUserSettings()

  const currentLanguage = user?.userSettings?.languagePreferences || "en"
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage)

  const hasUnsavedChanges = selectedLanguage !== currentLanguage

  const handleSave = () => {
    // Immediately change the language in i18n for instant feedback
    i18n.changeLanguage(selectedLanguage)

    updateSettings({
      languagePreferences: selectedLanguage as "en" | "es",
    })
  }

  return (
    <div className="flex flex-col gap-7.5">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl font-semibold text-primary leading-5 uppercase section-heading">
          {t("language.title")}
        </h1>
        <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("language.description")}</p>
      </div>

      <div className="flex flex-col gap-8.75 md:max-w-md">
        <div className="space-y-2">
          <Label className="text-xs uppercase text-stone">{t("language.title")} *</Label>
          <Select
            value={selectedLanguage}
            onValueChange={value => setSelectedLanguage(value as "en" | "es")}
          >
            <SelectTrigger className="w-full border-b rounded-none">
              <SelectValue placeholder={t("language.selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent className="w-full">
              {getLanguages().map(lang => (
                <SelectItem key={lang.value} value={lang.value}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={!hasUnsavedChanges}
          isLoading={isPending}
          className="py-3.75 w-full flex-1 md:w-[220px] h-12.5 uppercase"
        >
          {t("buttons.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  )
}
